import React from 'react';
import { tierColor, tierLabel, type Tier } from '../theme/tokens';
import clsx from 'clsx';

interface PillProps {
  tier: Tier;
  label?: string;
  size?: 'sm' | 'md';
  solid?: boolean;
  suffix?: string;
}

/**
 * Confidence pill — the signature element. Dot + label, in a bordered or solid pill.
 * Solid pills use the tier's bg color with the tier color as ink (used on the photo header overlay).
 */
export const Pill: React.FC<PillProps> = ({ tier, label, size = 'md', solid, suffix }) => {
  const { fg, bg } = tierColor(tier);
  const text = label ?? tierLabel(tier);

  if (solid) {
    return (
      <span
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-pill font-sans font-medium',
          size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]'
        )}
        style={{ backgroundColor: '#FFFFFFE6', color: fg, border: `1px solid ${fg}` }}
      >
        <span
          className="block rounded-full"
          style={{
            width: size === 'sm' ? 5 : 6,
            height: size === 'sm' ? 5 : 6,
            backgroundColor: fg,
          }}
        />
        <span className="uppercase tracking-wider">{text}</span>
        {suffix ? <span className="opacity-70">· {suffix}</span> : null}
      </span>
    );
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-pill font-sans font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]'
      )}
      style={{ backgroundColor: bg, color: fg, border: `1px solid ${fg}33` }}
    >
      <span
        className="block rounded-full"
        style={{
          width: size === 'sm' ? 5 : 6,
          height: size === 'sm' ? 5 : 6,
          backgroundColor: fg,
        }}
      />
      <span className="uppercase tracking-wider">{text}</span>
      {suffix ? <span className="opacity-70">· {suffix}</span> : null}
    </span>
  );
};
